import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronRight, Mail, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS, COMPANY_INFO, getLocalizedPath } from '@/lib/index';
import { useLanguage } from '@/hooks/useLanguage';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface NavItem {
  label: { jp: string; en: string };
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: { jp: 'ホーム', en: 'Home' }, path: ROUTE_PATHS.HOME },
  { label: { jp: '会社概要', en: 'Company' }, path: ROUTE_PATHS.COMPANY },
  { label: { jp: '理念', en: 'Philosophy' }, path: ROUTE_PATHS.PHILOSOPHY },
  { label: { jp: '代表挨拶', en: 'Founder' }, path: ROUTE_PATHS.FOUNDER },
  { label: { jp: '事業内容', en: 'Business' }, path: ROUTE_PATHS.BUSINESS },
  { label: { jp: 'コンプライアンス', en: 'Compliance' }, path: ROUTE_PATHS.COMPLIANCE },
  { label: { jp: '多文化対応', en: 'Multicultural' }, path: ROUTE_PATHS.MULTICULTURAL },
  { label: { jp: 'お問い合わせ', en: 'Contact' }, path: ROUTE_PATHS.CONTACT },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, t, changeLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const toggleLang = () => {
    const nextLang = language === 'jp' ? 'en' : 'jp';
    changeLanguage(nextLang);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background selection:bg-primary/10 selection:text-primary">
      {/* Navigation Header */}
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b',
          isScrolled
            ? 'bg-background/80 backdrop-blur-md border-border/40 py-3 shadow-sm'
            : 'bg-transparent border-transparent py-5'
        )}
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <Link
            to={getLocalizedPath(ROUTE_PATHS.HOME, language)}
            className="flex flex-col group"
          >
            <span className="text-xl font-bold tracking-tight text-primary transition-colors">
              {t(COMPANY_INFO.name)}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground group-hover:text-primary transition-colors">
              Osaka / Japan / Global
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <div className="flex items-center gap-6">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={getLocalizedPath(item.path, language)}
                  className={({ isActive }) =>
                    cn(
                      'text-sm font-medium transition-colors hover:text-primary relative py-1',
                      isActive
                        ? 'text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary'
                        : 'text-muted-foreground'
                    )
                  }
                >
                  {t(item.label)}
                </NavLink>
              ))}
            </div>

            <div className="h-4 w-px bg-border mx-2" />

            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLang}
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider hover:bg-accent"
            >
              <Globe className="w-4 h-4" />
              {language === 'jp' ? 'English' : '日本語'}
            </Button>
          </nav>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center gap-4 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLang}
              className="text-primary"
            >
              <Globe className="w-5 h-5" />
            </Button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-primary focus:outline-none"
              aria-label="Toggle Menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-0 z-40 lg:hidden bg-background pt-24 px-6"
          >
            <nav className="flex flex-col gap-6">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={getLocalizedPath(item.path, language)}
                  className={({ isActive }) =>
                    cn(
                      'text-2xl font-semibold flex items-center justify-between transition-colors',
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    )
                  }
                >
                  {t(item.label)}
                  <ChevronRight className="w-5 h-5 opacity-30" />
                </NavLink>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Corporate Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Profile Column */}
            <div className="space-y-6">
              <div className="flex flex-col">
                <span className="text-xl font-bold tracking-tight">
                  {t(COMPANY_INFO.name)}
                </span>
                <span className="text-[10px] uppercase tracking-widest opacity-60">
                  Cross-Border Enterprise
                </span>
              </div>
              <p className="text-sm opacity-80 leading-relaxed">
                {t({
                  jp: '誠実な事業運営と多文化理解を通じて、長期的な価値を創出する日本法人。',
                  en: 'A Japanese corporation creating long-term value through sincere business operations and multicultural understanding.',
                })}
              </p>
            </div>

            {/* Quick Links Column */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest mb-6 border-b border-primary-foreground/20 pb-2 inline-block">
                {t({ jp: 'ナビゲーション', en: 'Navigation' })}
              </h4>
              <ul className="space-y-3">
                {NAV_ITEMS.slice(0, 5).map((item) => (
                  <li key={item.path}>
                    <Link
                      to={getLocalizedPath(item.path, language)}
                      className="text-sm opacity-70 hover:opacity-100 transition-opacity flex items-center gap-2"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {t(item.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Information Column */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest mb-6 border-b border-primary-foreground/20 pb-2 inline-block">
                {t({ jp: '連絡先', en: 'Contact' })}
              </h4>
              <div className="space-y-4 text-sm opacity-80">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{t(COMPANY_INFO.location)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 shrink-0" />
                  <a href={`mailto:${COMPANY_INFO.email}`} className="hover:underline">
                    {COMPANY_INFO.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Brand Links Column */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest mb-6 border-b border-primary-foreground/20 pb-2 inline-block">
                {t({ jp: '運営ブランド', en: 'Our Brand' })}
              </h4>
              <div className="space-y-4">
                <a
                  href={COMPANY_INFO.catHotelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group p-4 border border-primary-foreground/20 rounded-lg hover:bg-primary-foreground/10 transition-colors"
                >
                  <p className="text-xs font-bold mb-1 uppercase tracking-tighter">
                    Cat Retreat
                  </p>
                  <p className="text-lg font-serif italic mb-2">猫じゃらし</p>
                  <p className="text-[10px] opacity-60 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Official Brand Website <ChevronRight className="w-2 h-2" />
                  </p>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-primary-foreground/10 space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="space-y-1">
                <p className="text-sm font-bold text-primary-foreground">株式会社M.M.K</p>
                <p className="text-xs opacity-70">M.M.K Corporation — Osaka Japan</p>
                <p className="text-xs opacity-70">Contact: mmk221.2025@gmail.com</p>
              </div>
              <div className="flex gap-6 text-[10px] opacity-50 uppercase tracking-[0.2em]">
                <span>Physical Business</span>
                <span>Compliance</span>
                <span>Long-term Operation</span>
              </div>
            </div>
            <div className="text-center">
              <p className="text-[10px] opacity-50 uppercase tracking-[0.2em]">{COMPANY_INFO.copyright}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
